import { useState, useContext, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { MapContext, MapView } from 'providers/MapProvider';
import { DataContext } from 'providers/DataProvider';
import { ME_QUERY, JOIN_REQUEST_MUTATION, ACTIVITY_QUERY, HIDE_JOIN_REQUEST_MUTATION, APPROVE_JOIN_REQUEST_MUTATION, DELETE_ACTIVITY_MUTATION } from 'graphql/APICalls';
import { useDialogState, DialogBox, DialogBoxContext } from 'components/Modals';

import 'styles/ActivityPage.css';
import { GroupsDisplay } from 'components/Helpers';



function JoinRequestCard({request, activityId, activityShortId}) {

  const navigate = useNavigate();
  
  const [hideJoinRequest] = useMutation(HIDE_JOIN_REQUEST_MUTATION);

  async function handleHideJoinRequest(e, joinRequest) {
    e.preventDefault();
    hideJoinRequest({variables: {id: joinRequest.id},
                    refetchQueries: [{query: ACTIVITY_QUERY, variables: {id: activityId, shortId: activityShortId}}]})
  };


  const [approveJoinRequest] = useMutation(APPROVE_JOIN_REQUEST_MUTATION);

  async function handleApproveJoinRequest(e, joinRequest) {
    e.preventDefault();
    approveJoinRequest({
      variables: {joinRequestId: joinRequest.id},
      refetchQueries: [{query: ACTIVITY_QUERY, variables: {id: activityId, shortId: activityShortId}}]
    })
  };

  return (
    <div className="join-request-card" key={request.id}>
      <div className="header">
        <label className="username" onClick={()=>{navigate(`/u/${request.applicant.username}`)}}>
          <b>{request.applicant.name}</b>
        </label>
        <div className="buttons">
          <button className="approve" onClick={e => handleApproveJoinRequest(e, request)}/>
          <button className="ignore"  onClick={e => handleHideJoinRequest(e, request)}/>
        </div>
      </div>
      {
        request.message ? 
        <label className="body">{request.message}</label>
        : null
      }
    </div>
  )

}


























export const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
  weekday: 'short', // "Wed"
  month: 'short',   // "Jan"
  day: 'numeric',   // "1"
  year: 'numeric',  // "2025"
  hour: 'numeric',  // "1" or "11"
  minute: 'numeric',// "00"
  hour12: true      // AM/PM format
});

export default function ActivityPage(){

  const { me } = useContext(DataContext);

  const { activityId, activityShortId } = useParams();

  const activityQuery = useQuery(ACTIVITY_QUERY, {variables: {id: activityId, shortId: activityShortId}});

  const activity = useMemo(() =>
    activityQuery.data?.getActivity
  , [activityQuery]);

  const navigate = useNavigate();

  const joinRequestDialogState = useDialogState();

  useEffect(() => {
    if (joinRequestDialogState.view === 'done') activityQuery.refetch()
  }, [joinRequestDialogState.view, activityQuery]);

  const mapViewOptions = useMemo(() => ({
    locationSelector: false,
    activityMarkers: false,
    geocoder: false,
    view: activity ? {center: activity.location.geometry.coordinates, zoom: 14} : null,
    indicatedActivity: activity
  }), [activity]);

  const handleJoinActivity = () => {
    if (joinRequestDialogState.view !== 'done') {
      if (me.username === undefined) { joinRequestDialogState.setView('login')}
      else { joinRequestDialogState.setView('initial') }
    }
    joinRequestDialogState.setVisible(true)
  };

  const undecidedJoinRequests = useMemo(() => {
    return (activity?.joinRequests || []).filter(request =>
      request.status === "undecided"
    )
  }, [activity]);

  const deleteActivityDialogState = useDialogState();

  return (
    <>
    <DialogBox {...{dialogState: joinRequestDialogState}}>
      <JoinRequestDialogBody {...{activity}}/>
    </DialogBox>
    <DialogBox {...{dialogState: deleteActivityDialogState}}>
      <DeleteActivityDialogBody {...{activity}}/>
    </DialogBox>
    <div className="auto-scrollbar">
    <div className="centerer activity-page-overlay">
      <div className="activity-page-data">


        { 
          activity?.picture &&
          <div className="activity-page-banner" style={{backgroundImage: `url(${activity?.picture.imageSrc})`}}/>
        }


        <div className="activity-page-title-buttons-backdrop"/>


        <label className="activity-page-title">{activity?.title || '\u00A0'}</label>


        <div className="activity-buttons">
          {
            <button className="share-button" onClick={()=>{
              navigator.clipboard.writeText(window.location.href)
            }}/>
          }
          {
            activity && ((activity.userRole === null) || (activity?.userRole === 'stranger')) &&
            <button className="join-button" onClick={handleJoinActivity}>
              Join {activity?.creator.name}
            </button>
          }
          {
            ['creator','participant'].includes(activity?.userRole) &&
            <button className="chat-button" onClick={()=>{navigate(`/tc/${activity?.shortId}`)}}/>
          }
          {
            activity?.userRole === 'requester' &&
            <button className="message-sent-indicator"/>
          }
          {
            activity?.userRole === 'creator' &&
            <button className="delete-button" onClick={()=>{deleteActivityDialogState.setVisible(true)}}/>
          }
        </div>


        <label className="activity-page-creator">
          <i>with </i>
          {
            activity?.userRole === 'creator' ? `${activity?.creator.name} (You)` :
            <Link to={"/u/"+activity?.creator.username}>{activity?.creator.name}</Link>
          }
        </label>


        <label className="activity-page-description">
          <div className="balloon-tip"/>
          <div className="balloon">
            {activity?.description || <span style={{color: 'gray'}}>(No description)</span>}
          </div>
        </label>


        <div className="activity-invitees">
          <label style={{paddingTop: '2px'}}><i>Invited:</i></label>
          <GroupsDisplay groups={activity?.groups} buttonStyle={"group-button-2"} noGroupsMessage={<div>Everyone</div>}/>
        </div>


        <label className="activity-location">
          <div className="name">
            <i>at </i>
            <label>{activity?.location.name}</label>
          </div>
          <div className="map">
            <MapView id={"activity_page_map"} options={mapViewOptions}/>
          </div>
        </label>


        <label className="activity-time">
          <label><i>Starts</i></label>
          <label>
            {
              activityQuery.data
              ? (activity?.startDate
                  ? dateTimeFormat.format(new Date(activity?.startDate))
                  : 'ongoing')
              : null
            }
          </label>
          <label><i>Ends</i></label>
          <label>
            {
            activityQuery.data
              ? (activity?.endDate
                  ? dateTimeFormat.format(new Date(activity?.endDate))
                  : 'never')
              : null
            }
          </label>
        </label>


        {
          activity?.userRole === "creator" ?
          <label className="activity-page-applicants">
            <label><i>Interested</i></label>
            {
              undecidedJoinRequests.length > 0 ?
              <div className="display">
                {activity ? undecidedJoinRequests.map(
                  request => <JoinRequestCard {...{request, activityId, activityShortId}} />
                ) : null}
              </div>

              : <div style={{color: 'gray'}}>(No undecided requests to show yet)</div>
            }
          </label>
          : null
        }

      </div>
    </div>
    </div>
    </>
  )
}



function JoinRequestDialogBody({activity}) {

  const dialogState = useContext(DialogBoxContext);

  const navigate = useNavigate();

  const [userMessage, setUserMessage] = useState('');

  const [sendJoinRequestMutation, joinRequestMutation] = useMutation(JOIN_REQUEST_MUTATION, {
    variables: {activityId: activity?.id, message: userMessage},
    refetchQueries: [ { query: ME_QUERY } ] // TODO: make this more efficient than refetching all data about the user
  });
  
  useEffect(() => {
    if (joinRequestMutation.data?.makeJoinRequest) dialogState.setView('done');
  }, [joinRequestMutation, dialogState]);

  useEffect(() => {
    if (!dialogState.visible) setUserMessage('');
  }, [dialogState.visible]);

  return (
      dialogState.view==='initial' ?
        <div>
          <p>We'll let {activity?.creator.name} know you want to join!</p>
          <p>Would you like to add a message?</p>
          <button onClick={()=>{dialogState.setView('message')}}>Yes</button>
          <button onClick={sendJoinRequestMutation}>No, send without message</button>
        </div>
      
      :dialogState.view==='message' ?
        <div>
          <p>Write your message below:</p>
          <textarea value={userMessage} onChange={e => setUserMessage(e.target.value)}/>
          <button onClick={()=>{setUserMessage(''); dialogState.setView('initial')}}>Back</button>
          <button onClick={sendJoinRequestMutation}>Send</button>
        </div>
      
      :dialogState.view==='done' ?
        <div className="done-message">Message sent!</div>
      
      :dialogState.view==='login' ?
        <div className="login-message">
          <p>You'll need an account to message {activity.creator.name}.</p>
          <button onClick={()=>{navigate('/login')}}>Log in</button>
          <button onClick={()=>{navigate('/signup')}}>Sign up</button>
        </div>
      
      : <div>Error</div>
  )
}

function DeleteActivityDialogBody({activity}) {

  const dialogState        = useContext(DialogBoxContext);
  const { updateLocation } = useContext(MapContext);
  
  const navigate = useNavigate();

  const [deleteActivity] = useMutation(DELETE_ACTIVITY_MUTATION);

  return (
    <div>
      <p>Are you sure you want to delete this activity?</p>
      <button onClick={()=>{
          deleteActivity({
            variables: {id: activity.id},
            onCompleted: ()=>{updateLocation()},
            refetchQueries: [{query: ME_QUERY}]
          });
          navigate('/');
        }}>Yes</button>
      <button onClick={()=>{dialogState.setVisible(false)}}>No</button>
    </div>
  )
}